import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, ComboBox, useDialog } from '@gokrew/ui';
import { Trash2 } from 'lucide-react';
import { deleteCrmContact, getCrmCompanies, saveCrmContact } from '@/api/crm';
import type { CrmCompany, CrmPerson, CrmPersonInput } from '@/api/crm';
import { useCrmFormat } from './crm-format';
import { CrmCompanyEditor } from './CrmCompanyEditor';
import { CrmDeleteDialog } from './CrmDeleteDialog';
import { CrmButton, CrmRequired } from './CrmShared';
import styles from './Crm.module.css';
export const CrmContactEditor = ({
  contact,
  dialogId,
  companyId,
  initialName = '',
  onSaved,
}: {
  contact?: CrmPerson;
  dialogId?: string;
  companyId?: string;
  /** Prefills a new contact: first word = first name, the rest = last name. */
  initialName?: string;
  onSaved?: (contact: CrmPerson) => void;
}) => {
  const { t } = useCrmFormat();
  const client = useQueryClient();
  const { openDialog, closeDialog } = useDialog();
  const [search, setSearch] = useState('');
  const [created, setCreated] = useState<CrmCompany | null>(null);
  const [first = '', ...rest] = initialName.trim().split(/\s+/);
  const [form, setForm] = useState<CrmPersonInput>(
    contact
      ? {
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.phone,
          title: contact.title,
          companyId: contact.companyId,
          version: contact.version,
        }
      : {
          firstName: first,
          lastName: rest.join(' '),
          email: '',
          phone: '',
          title: '',
          companyId: companyId ?? null,
        }
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const companies = useQuery({
    queryKey: ['crm-companies', 'select', search],
    queryFn: () => getCrmCompanies(search, 1),
  });
  const options = (companies.data?.items ?? []).map((company) => ({
    value: company.id,
    label: company.name,
  }));
  if (
    contact?.company &&
    !options.some((option) => option.value === contact.company!.id)
  )
    options.unshift({ value: contact.company.id, label: contact.company.name });
  if (created && !options.some((option) => option.value === created.id))
    options.unshift({ value: created.id, label: created.name });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const saved = await saveCrmContact(
        {
          ...form,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email?.trim() || null,
          phone: form.phone?.trim() || '',
          title: form.title?.trim() || '',
        },
        contact?.id
      );
      await client.invalidateQueries({ queryKey: ['crm'] });
      onSaved?.(saved);
      if (dialogId) closeDialog(dialogId);
    } catch (err) {
      setError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.conflict'
            : 'crm.save_error'
        )
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <fieldset className={styles.grid} disabled={busy}>
        {(['firstName', 'lastName', 'email', 'phone', 'title'] as const).map(
          (key) => (
            <label key={key}>
              <span>
                {t(`crm.contact_${key}`)}
                {(key === 'firstName' || key === 'lastName') && <CrmRequired />}
              </span>
              <Input
                autoFocus={key === 'firstName'}
                required={key === 'firstName' || key === 'lastName'}
                maxLength={
                  key === 'email'
                    ? 254
                    : key === 'phone'
                      ? 50
                      : key === 'title'
                        ? 200
                        : 100
                }
                type={
                  key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'
                }
                value={form[key] ?? ''}
                onChange={(event) =>
                  setForm((old) => ({ ...old, [key]: event.target.value }))
                }
              />
            </label>
          )
        )}
        <label className={styles.fullWidth}>
          {t('crm.workspace.company')}
          <ComboBox
            size="sm"
            value={form.companyId ?? ''}
            placeholder={t('crm.workspace.choose_company')}
            options={options}
            onSearch={setSearch}
            onChange={(value) =>
              setForm((old) => ({ ...old, companyId: value || null }))
            }
            addNewPosition="top"
            addNewLabel={
              search.trim()
                ? t('crm.create_named', { name: search.trim() })
                : t('crm.add_company')
            }
            onAddNew={() =>
              openDialog({
                title: t('crm.add_company'),
                containerClass: styles.editorDialog,
                content: (
                  <CrmCompanyEditor
                    initialName={search}
                    onSaved={(company) => {
                      setCreated(company);
                      setForm((old) => ({ ...old, companyId: company.id }));
                    }}
                  />
                ),
              })
            }
          />
        </label>
      </fieldset>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.actions}>
        {contact && (
          <CrmButton
            type="button"
            variant="destructive"
            IconPrefix={Trash2}
            className={styles.editorDelete}
            disabled={busy}
            onClick={() =>
              openDialog({
                title: t('crm.delete_contact_title'),
                content: (
                  <CrmDeleteDialog
                    description={t('crm.delete_contact_description', {
                      name: contact.name,
                    })}
                    conflictKey="crm.delete_changed"
                    onDelete={() => deleteCrmContact(contact)}
                    editorDialogId={dialogId}
                  />
                ),
              })
            }
          >
            {t('crm.delete')}
          </CrmButton>
        )}
        <CrmButton
          variant="outline"
          type="button"
          onClick={() => dialogId && closeDialog(dialogId)}
          disabled={busy}
        >
          {t('crm.cancel')}
        </CrmButton>
        <CrmButton
          variant="adminPrimary"
          type="submit"
          disabled={busy || !form.firstName.trim() || !form.lastName.trim()}
        >
          {t(busy ? 'crm.saving' : 'crm.save')}
        </CrmButton>
      </div>
    </form>
  );
};
